import { useCallback, useEffect, useRef, useState } from "react";
import { onboardingService } from "../services/onboarding.service";
import {
  buildSurveyPayload,
  normalizeSurveyQuestions,
  SurveySchemaError,
  validateQuestionAnswer,
} from "../services/onboardingSurveyAdapter";

const unwrapData = (value) =>
  value && typeof value === "object" && value.data !== undefined
    ? value.data
    : value;

const errorStatus = (error) =>
  error && error.response ? error.response.status : error && error.status;

const getApiMessage = (error) => {
  const apiError =
    error && error.response && error.response.data && error.response.data.error;
  if (!apiError) return "";

  if (Array.isArray(apiError.details)) {
    return apiError.details
      .map((detail) => detail && detail.message)
      .filter(Boolean)
      .join(" ");
  }
  return typeof apiError.message === "string" ? apiError.message : "";
};

const validSurveyId = (value) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

function resolveIdentity(user, email) {
  const id = user && (user.id ?? user.usuarioId ?? user.userId);
  return {
    email,
    id: id === undefined || id === null || id === "" ? null : String(id),
  };
}

function sameIdentity(left, right) {
  if (!left || !right || left.email !== right.email) return false;
  return !left.id || !right.id || left.id === right.id;
}

function sameSurveyScope(scope, identity, surveyId) {
  return Boolean(
    scope &&
      scope.surveyId === surveyId &&
      sameIdentity(scope.identity, identity)
  );
}

function sameQuestionSchema(previous, next) {
  if (
    !previous ||
    previous.id !== next.id ||
    previous.code !== next.code ||
    previous.type !== next.type ||
    previous.prompt !== next.prompt ||
    previous.options.length !== next.options.length
  ) {
    return false;
  }
  return previous.options.every(
    (option, index) =>
      option.id === next.options[index].id &&
      option.label === next.options[index].label
  );
}

function retainCompatibleAnswers(previousQuestions, previousAnswers, nextQuestions) {
  const previousById = new Map(
    previousQuestions.map((question) => [question.id, question])
  );
  return nextQuestions.reduce((retained, question) => {
    const previous = previousById.get(question.id);
    const answer = previousAnswers[question.id];
    if (
      answer !== undefined &&
      sameQuestionSchema(previous, question) &&
      !validateQuestionAnswer(question, answer)
    ) {
      retained[question.id] = answer;
    }
    return retained;
  }, {});
}

export function useOnboardingFlow(service = onboardingService) {
  const [stage, setStage] = useState("login");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState(null);
  const [surveyStatus, setSurveyStatus] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [isBusy, setIsBusy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordWasUpdated, setPasswordWasUpdated] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [needsReauthentication, setNeedsReauthentication] = useState(false);
  const submitLock = useRef(false);
  const flowEpoch = useRef(0);
  const activeToken = useRef(null);
  const identity = useRef(null);
  const surveyScope = useRef(null);
  const questionsRef = useRef([]);
  const answersRef = useRef({});
  const questionIndexRef = useRef(0);
  const pendingSubmissionSurveyId = useRef(null);

  const isCurrentRequest = useCallback((requestEpoch, requestToken, requestIdentity) => {
    return (
      requestEpoch === flowEpoch.current &&
      (!requestToken || activeToken.current === requestToken) &&
      (!requestIdentity || sameIdentity(identity.current, requestIdentity))
    );
  }, []);

  const clearDraft = useCallback((nextScope = null) => {
    surveyScope.current = nextScope;
    questionsRef.current = [];
    answersRef.current = {};
    questionIndexRef.current = 0;
    setQuestions([]);
    setAnswers({});
    setQuestionIndex(0);
    setFieldErrors({});
  }, []);

  useEffect(
    () => () => {
      flowEpoch.current += 1;
    },
    []
  );

  const moveToLogin = useCallback((message, reauthentication = true) => {
    activeToken.current = null;
    setToken(null);
    setNeedsReauthentication(reauthentication);
    setStage("login");
    setError(message);
    setIsBusy(false);
    setIsSubmitting(false);
  }, []);

  const loadSurvey = useCallback(
    async (activeToken) => {
      const requestEpoch = flowEpoch.current;
      const requestIdentity = identity.current;
      setIsBusy(true);
      setError("");
      try {
        const status = unwrapData(await service.getSurveyStatus(activeToken));
        if (!isCurrentRequest(requestEpoch, activeToken, requestIdentity)) return false;
        if (!status || typeof status.completed !== "boolean") {
          throw new SurveySchemaError(
            "No pudimos leer el estado de la ficha. Intenta comprobarlo otra vez.",
            "INVALID_SURVEY_STATUS"
          );
        }

        const surveyId = validSurveyId(status.surveyId);
        if (!surveyId || !["M", "F"].includes(status.genero)) {
          throw new SurveySchemaError(
            "El servidor no devolvió una ficha válida para esta cuenta. No se guardó ninguna respuesta.",
            "INVALID_SURVEY_REFERENCE"
          );
        }

        const pendingSurveyId = validSurveyId(
          pendingSubmissionSurveyId.current
        );
        if (pendingSurveyId && surveyId !== pendingSurveyId) {
          setStage("uncertain");
          setError(
            "El estado recibido después de iniciar sesión corresponde a otra ficha. Conservamos tus respuestas y no podemos confirmar el envío anterior todavía."
          );
          return false;
        }

        const scopeMatches = sameSurveyScope(
          surveyScope.current,
          requestIdentity,
          surveyId
        );
        if (!scopeMatches) {
          clearDraft({ identity: requestIdentity, surveyId });
        }
        setSurveyStatus(status);
        setAlreadyCompleted(status.completed);
        if (status.completed) {
          clearDraft({ identity: requestIdentity, surveyId });
          pendingSubmissionSurveyId.current = null;
          setStage("success");
          return true;
        }

        const questionsResponse = unwrapData(
          await service.getSurveyQuestions(activeToken, surveyId)
        );
        if (!isCurrentRequest(requestEpoch, activeToken, requestIdentity)) return false;
        if (
          questionsResponse &&
          questionsResponse.encuestaId !== undefined &&
          validSurveyId(questionsResponse.encuestaId) !== surveyId
        ) {
          throw new SurveySchemaError(
            "La ficha recibida no corresponde al estado de esta cuenta. No se guardó ninguna respuesta.",
            "SURVEY_MISMATCH"
          );
        }

        const normalizedQuestions = normalizeSurveyQuestions(
          questionsResponse && questionsResponse.preguntas
        );
        const retainedAnswers = scopeMatches
          ? retainCompatibleAnswers(
              questionsRef.current,
              answersRef.current,
              normalizedQuestions
            )
          : {};
        const nextQuestionIndex = scopeMatches
          ? Math.min(questionIndexRef.current, normalizedQuestions.length - 1)
          : 0;
        surveyScope.current = { identity: requestIdentity, surveyId };
        questionsRef.current = normalizedQuestions;
        answersRef.current = retainedAnswers;
        questionIndexRef.current = nextQuestionIndex;
        pendingSubmissionSurveyId.current = null;
        setQuestions(normalizedQuestions);
        setAnswers(retainedAnswers);
        setQuestionIndex(nextQuestionIndex);
        setFieldErrors({});
        setStage("survey");
        return true;
      } catch (loadError) {
        if (!isCurrentRequest(requestEpoch, activeToken, requestIdentity)) return false;
        if (errorStatus(loadError) === 401) {
          moveToLogin(
            "Tu sesión venció. Inicia sesión de nuevo para continuar; el borrador seguirá solo en esta pestaña.",
            true
          );
        } else if (loadError instanceof SurveySchemaError) {
          setStage("blocked");
          setError(loadError.message);
        } else {
          setStage("recovery");
          setError(
            "No pudimos comprobar el estado de la ficha. Reintenta cuando tengas conexión."
          );
        }
        return false;
      } finally {
        if (isCurrentRequest(requestEpoch, activeToken, requestIdentity)) {
          setIsBusy(false);
        }
      }
    },
    [clearDraft, isCurrentRequest, moveToLogin, service]
  );

  const login = useCallback(
    async ({ email: submittedEmail, password, alreadyUpdated }) => {
      flowEpoch.current += 1;
      const requestEpoch = flowEpoch.current;
      activeToken.current = null;
      setToken(null);
      setIsBusy(true);
      setError("");
      const normalizedEmail = String(submittedEmail || "").trim().toLowerCase();
      setEmail(normalizedEmail);

      try {
        const loginBody = unwrapData(
          await service.login(normalizedEmail, password)
        );
        if (!isCurrentRequest(requestEpoch)) return false;
        const nextToken = loginBody && loginBody.sessiontoken;
        if (!nextToken) {
          throw new Error("La respuesta de acceso no incluyó sesión.");
        }

        let loginUser = loginBody.user || {};
        let role = loginUser.rol !== undefined ? loginUser.rol : loginUser.role;
        if (role === undefined || role === null) {
          const profile = unwrapData(await service.getMyProfile(nextToken));
          if (!isCurrentRequest(requestEpoch)) return false;
          loginUser = profile || loginUser;
          role = profile && profile.rol;
        }

        if (Number(role) !== 3) {
          activeToken.current = null;
          setToken(null);
          setNeedsReauthentication(false);
          setError("Esta ruta está disponible para cuentas de colaboradores.");
          return false;
        }

        const nextIdentity = resolveIdentity(loginUser, normalizedEmail);
        if (!sameIdentity(identity.current, nextIdentity)) {
          clearDraft();
          pendingSubmissionSurveyId.current = null;
        }
        identity.current = nextIdentity;
        activeToken.current = nextToken;
        setToken(nextToken);
        setNeedsReauthentication(false);
        if (alreadyUpdated) {
          return await loadSurvey(nextToken);
        }

        setError("");
        setStage("password");
        return true;
      } catch (loginError) {
        if (!isCurrentRequest(requestEpoch)) return false;
        if (errorStatus(loginError) === 401 || errorStatus(loginError) === 400) {
          setError("No pudimos iniciar sesión con esos datos. Revisa el correo y la contraseña.");
        } else {
          setError("No pudimos iniciar sesión. Revisa tu conexión e inténtalo otra vez.");
        }
        return false;
      } finally {
        if (isCurrentRequest(requestEpoch)) setIsBusy(false);
      }
    },
    [clearDraft, isCurrentRequest, loadSurvey, service]
  );

  const changePassword = useCallback(
    async (password) => {
      if (password.length < 6) {
        setError("La contraseña debe tener al menos 6 caracteres.");
        return false;
      }

      const requestEpoch = flowEpoch.current;
      const requestToken = token;
      const requestIdentity = identity.current;
      setIsBusy(true);
      setError("");
      try {
        await service.changePassword(requestToken, password);
        if (!isCurrentRequest(requestEpoch, requestToken, requestIdentity)) return false;
        setPasswordWasUpdated(true);
        return await loadSurvey(requestToken);
      } catch (passwordError) {
        if (!isCurrentRequest(requestEpoch, requestToken, requestIdentity)) return false;
        const status = errorStatus(passwordError);
        if (status === 401) {
          moveToLogin(
            "La sesión venció antes de confirmar el cambio. Inicia sesión con tu contraseña actual para continuar.",
            false
          );
        } else if (!passwordError.response || status >= 500) {
          moveToLogin(
            "No recibimos confirmación del cambio. Prueba iniciar sesión con la contraseña nueva; si no funciona, usa la anterior y vuelve a cambiarla.",
            false
          );
        } else {
          setError(
            "No pudimos actualizar la contraseña. Revisa los datos e inténtalo otra vez."
          );
        }
        return false;
      } finally {
        if (isCurrentRequest(requestEpoch, requestToken, requestIdentity)) {
          setIsBusy(false);
        }
      }
    },
    [isCurrentRequest, loadSurvey, moveToLogin, service, token]
  );

  const setAnswer = useCallback((questionId, answer) => {
    const nextAnswers = { ...answersRef.current, [questionId]: answer };
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
    setFieldErrors((currentErrors) => {
      if (!currentErrors[questionId]) return currentErrors;
      const nextErrors = { ...currentErrors };
      delete nextErrors[questionId];
      return nextErrors;
    });
    setError("");
  }, []);

  const continueSurvey = useCallback(() => {
    const question = questions[questionIndex];
    if (!question) return;
    const validationMessage = validateQuestionAnswer(question, answers[question.id]);
    if (validationMessage) {
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        [question.id]: validationMessage,
      }));
      return;
    }

    setFieldErrors({});
    if (questionIndex < questions.length - 1) {
      const nextIndex = questionIndex + 1;
      questionIndexRef.current = nextIndex;
      setQuestionIndex(nextIndex);
    } else {
      setStage("review");
    }
  }, [answers, questionIndex, questions]);

  const goBack = useCallback(() => {
    if (stage === "review") {
      setStage("survey");
      const previousIndex = Math.max(questions.length - 1, 0);
      questionIndexRef.current = previousIndex;
      setQuestionIndex(previousIndex);
      return;
    }
    const previousIndex = Math.max(questionIndex - 1, 0);
    questionIndexRef.current = previousIndex;
    setQuestionIndex(previousIndex);
    setError("");
  }, [questionIndex, questions.length, stage]);

  const reconcileSubmission = useCallback(
    async (activeToken = token, expectedSurveyId) => {
      if (!activeToken) return false;
      const requestEpoch = flowEpoch.current;
      const requestIdentity = identity.current;
      const expectedId = validSurveyId(
        expectedSurveyId ??
          pendingSubmissionSurveyId.current ??
          (surveyStatus && surveyStatus.surveyId)
      );
      setIsBusy(true);
      setError("Comprobando si el servidor guardó la ficha…");
      try {
        const status = unwrapData(await service.getSurveyStatus(activeToken));
        if (!isCurrentRequest(requestEpoch, activeToken, requestIdentity)) return false;
        if (!status || typeof status.completed !== "boolean") {
          throw new Error("Estado de ficha incompleto");
        }
        if (
          !expectedId ||
          validSurveyId(status.surveyId) !== expectedId
        ) {
          throw new SurveySchemaError(
            "El estado recibido corresponde a otra ficha. Conservamos tus respuestas y no podemos confirmar este envío todavía.",
            "RECONCILE_SURVEY_MISMATCH"
          );
        }

        setSurveyStatus(status);
        if (status.completed) {
          setAlreadyCompleted(false);
          clearDraft({ identity: requestIdentity, surveyId: expectedId });
          pendingSubmissionSurveyId.current = null;
          setStage("success");
          setError("");
          return true;
        }

        setAlreadyCompleted(false);
        setStage("review");
        setError(
          "El servidor confirma que la ficha sigue pendiente. Revisa tus respuestas antes de volver a enviarla."
        );
        return false;
      } catch (reconcileError) {
        if (!isCurrentRequest(requestEpoch, activeToken, requestIdentity)) return false;
        if (errorStatus(reconcileError) === 401) {
          moveToLogin(
            "Tu sesión venció. Inicia sesión de nuevo y comprobaremos el estado antes de repetir el envío.",
            true
          );
        } else {
          setStage("uncertain");
          setError(
            reconcileError instanceof SurveySchemaError
              ? reconcileError.message
              : "No pudimos confirmar si la ficha se guardó. Comprueba el estado antes de intentar otro envío."
          );
        }
        return false;
      } finally {
        if (isCurrentRequest(requestEpoch, activeToken, requestIdentity)) {
          setIsBusy(false);
        }
      }
    },
    [clearDraft, isCurrentRequest, moveToLogin, service, surveyStatus, token]
  );

  const submitSurvey = useCallback(async () => {
    if (submitLock.current || isSubmitting) return false;
    const result = buildSurveyPayload(
      surveyStatus && surveyStatus.surveyId,
      questions,
      answers
    );
    if (Object.keys(result.errors).length > 0) {
      setFieldErrors(result.errors);
      const firstInvalidQuestion = questions.findIndex(
        (question) => result.errors[question.id]
      );
      setQuestionIndex(Math.max(firstInvalidQuestion, 0));
      questionIndexRef.current = Math.max(firstInvalidQuestion, 0);
      setStage("survey");
      setError("Revisa las respuestas marcadas antes de enviar la ficha.");
      return false;
    }
    if (!result.payload) {
      setError(result.message || "No hay respuestas para enviar.");
      return false;
    }

    submitLock.current = true;
    const requestEpoch = flowEpoch.current;
    const requestToken = token;
    const requestIdentity = identity.current;
    pendingSubmissionSurveyId.current = result.payload.encuestaId;
    setIsSubmitting(true);
    setError("");
    try {
      await service.saveSurvey(requestToken, result.payload);
      if (!isCurrentRequest(requestEpoch, requestToken, requestIdentity)) return false;
      await reconcileSubmission(requestToken, result.payload.encuestaId);
      return true;
    } catch (saveError) {
      if (!isCurrentRequest(requestEpoch, requestToken, requestIdentity)) return false;
      const status = errorStatus(saveError);
      if (status === 401) {
        moveToLogin(
          "Tu sesión venció durante el envío. Inicia sesión de nuevo y comprobaremos el estado antes de repetirlo.",
          true
        );
      } else if (status === 409 || !saveError.response || status >= 500) {
        setError("No recibimos una confirmación final. Comprobaremos el estado de la ficha.");
        await reconcileSubmission(requestToken, result.payload.encuestaId);
      } else {
        setStage("review");
        setError(
          getApiMessage(saveError) ||
            "No se pudo guardar la ficha. Revisa tus respuestas e inténtalo otra vez."
        );
      }
      return false;
    } finally {
      submitLock.current = false;
      if (isCurrentRequest(requestEpoch, requestToken, requestIdentity)) {
        setIsSubmitting(false);
      }
    }
  }, [
    answers,
    isSubmitting,
    isCurrentRequest,
    moveToLogin,
    questions,
    reconcileSubmission,
    service,
    surveyStatus,
    token,
  ]);

  const close = useCallback(() => {
    flowEpoch.current += 1;
    submitLock.current = false;
    activeToken.current = null;
    identity.current = null;
    surveyScope.current = null;
    pendingSubmissionSurveyId.current = null;
    setToken(null);
    setSurveyStatus(null);
    clearDraft();
    setEmail("");
    setError("");
    setFieldErrors({});
    setPasswordWasUpdated(false);
    setAlreadyCompleted(false);
    setNeedsReauthentication(false);
    setIsBusy(false);
    setIsSubmitting(false);
    setStage("closed");
  }, [clearDraft]);

  const retryStatus = useCallback(() => {
    if (token) return loadSurvey(token);
    return Promise.resolve(false);
  }, [loadSurvey, token]);

  return {
    stage,
    email,
    setEmail,
    token,
    surveyStatus,
    questions,
    answers,
    questionIndex,
    error,
    fieldErrors,
    isBusy,
    isSubmitting,
    passwordWasUpdated,
    alreadyCompleted,
    needsReauthentication,
    login,
    changePassword,
    setAnswer,
    continueSurvey,
    goBack,
    submitSurvey,
    reconcileSubmission,
    retryStatus,
    close,
  };
}

export default useOnboardingFlow;
